import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';
import { Spinner } from '../ui/spinner';
import { Card, CardContent } from '../ui/card';
import { ArrowLeft } from 'lucide-react';
import { chatbotAPI } from '../../services/chatbotService';
import TurnCard from './TurnCard';
import UserStatePanel from './UserStatePanel';

const ChatReview = () => {
  const { threadId } = useParams();
  const navigate = useNavigate();
  const [chatData, setChatData] = useState(null);
  const [evaluations, setEvaluations] = useState({});
  const [userState, setUserState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userStateLoading, setUserStateLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadChatData = async () => {
      try {
        setLoading(true); setError(''); setUserStateLoading(true);
        const historyResponse = await chatbotAPI.getChatHistory(threadId);
        const turns = historyResponse.data.turns || [];
        const userData = historyResponse.data.user_state || null;
        const evalResponse = await chatbotAPI.getEvaluations(threadId);
        const evalsMap = {};
        (evalResponse.data.evaluations || []).forEach((evaluation) => { evalsMap[evaluation.turn_index] = evaluation; });
        setChatData({ turns }); setEvaluations(evalsMap); setUserState(userData);
      } catch (err) {
        if (err.response?.status === 404) setError('Chat not found.');
        else if (err.response?.status === 403) setError('You do not have permission.');
        else setError('Failed to load chat data.');
      } finally { setLoading(false); setUserStateLoading(false); }
    };
    loadChatData();
  }, [threadId]);

  const handleEvaluationSave = async (turnIndex, data) => {
    try {
      if (evaluations[turnIndex]) { await chatbotAPI.updateEvaluation(evaluations[turnIndex].id, data); }
      else { const response = await chatbotAPI.saveEvaluation({ thread_id: threadId, turn_index: turnIndex, ...data }); evaluations[turnIndex] = { ...data, id: response.data.id }; setEvaluations({ ...evaluations }); }
    } catch (err) { console.error('Failed to save evaluation:', err); throw err; }
  };

  if (loading) return <div className="flex justify-center items-center min-h-screen"><Spinner size="lg" /></div>;
  if (error) return (
    <div className="max-w-5xl mx-auto py-6">
      <Alert variant="error"><AlertDescription>{error}</AlertDescription></Alert>
      <Button variant="ghost" onClick={() => navigate('/bot-evaluation')} className="mt-4 gap-2"><ArrowLeft className="h-4 w-4" />Back</Button>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto py-6">
      <Card className="mb-4">
        <CardContent className="p-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold mb-2">Chat Evaluation</h2>
              <div className="flex gap-2 flex-wrap">
                <Badge className="cursor-pointer" onClick={() => window.open(`https://my.livechatinc.com/archives/${threadId}`, '_blank')}>Thread: {threadId}</Badge>
                {userState?.data?.user?.data?.softswiss?.email && (
                  <Badge variant="info" className="cursor-pointer" onClick={() => window.open(`https://backoffice.bitstarz-kute.com/backend/players/${userState.data.user.data.softswiss.id}`, '_blank')}>
                    User: {userState.data.user.data.softswiss.email}
                  </Badge>
                )}
              </div>
            </div>
            <Button variant="ghost" onClick={() => navigate('/bot-evaluation')} className="gap-2"><ArrowLeft className="h-4 w-4" />Back</Button>
          </div>
        </CardContent>
      </Card>
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 md:col-span-8">
          {chatData?.turns.map((turn, index) => <TurnCard key={index} turnIndex={index} turn={turn} evaluation={evaluations[index]} onEvaluationSave={handleEvaluationSave} />)}
          {chatData?.turns.length === 0 && <Card><CardContent className="p-6 text-center text-ink-dim">No chat turns found.</CardContent></Card>}
        </div>
        <div className="col-span-12 md:col-span-4"><div className="sticky top-5"><UserStatePanel userState={userState} loading={userStateLoading} error="" /></div></div>
      </div>
    </div>
  );
};

export default ChatReview;
